import React, { useMemo } from 'react';

type ActiveGranit = {
  id: string;
  granit?: {
    id: string;
    name: string;
    reducedImage: string;
  };
};

type TextureSelectionProps = {
  hasSelectedAllPieces: boolean;
  setHasSelectedAllPieces: (value: boolean) => void;
  activeGranits: ActiveGranit[];
  selectedDominant?: string;
  setSelectedDominant: (id: string | undefined) => void;
  buckets?: { BUCKET_3D_PIECES?: string };
  activePieces: any[];
  pieceIndex: number;
  setValue: <T extends any>(stateName: string, value: T) => void;
  selectPieceWithGranitId: (granitId: string) => void;
};

export const TextureSelection: React.FC<TextureSelectionProps> = ({
  hasSelectedAllPieces,
  setHasSelectedAllPieces,
  activeGranits,
  selectedDominant,
  setSelectedDominant,
  buckets,
  activePieces,
  setValue,
  selectPieceWithGranitId,
}) => {
  const canSelectEveryPieces = useMemo(() => {
    const materials = activePieces.map((el) => el.material);
    const uniqueMaterials = [...new Set(materials)];
    return uniqueMaterials.length === 1;
  }, [activePieces]);

  const hasSelection = hasSelectedAllPieces || selectedDominant;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {/* Label */}
      <div className='flex items-center justify-between'>
        <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
          Appliquer à
        </span>
        {hasSelection && (
          <button
            onClick={() => {
              setHasSelectedAllPieces(false);
              setSelectedDominant(undefined);
              setValue('hasAdvancedMode', false);
            }}
            className='text-xs text-gray-400 hover:text-gray-600 transition-colors'
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Options de sélection */}
      <div className='flex flex-wrap gap-2'>
        {/* Bouton "Toutes les pièces" */}
        {canSelectEveryPieces && (
          <button
            onClick={() => {
              if (hasSelectedAllPieces) {
                setHasSelectedAllPieces(false);
                setSelectedDominant(undefined);
                setValue('hasAdvancedMode', false);
              } else {
                setHasSelectedAllPieces(true);
                setSelectedDominant(undefined);
                setValue('hasAdvancedMode', true);
              }
            }}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              hasSelectedAllPieces
                ? 'bg-turquoise text-white shadow-sm'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-turquoise hover:bg-turquoise-light'
            }`}
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z'
              />
            </svg>
            <span>Toutes les pièces</span>
          </button>
        )}

        {/* Dominantes */}
        {activeGranits.map((el) => {
          const isSelected = selectedDominant === el.id;
          return (
            <button
              key={el.id}
              onClick={() => {
                if (isSelected) {
                  setSelectedDominant(undefined);
                  setValue('hasAdvancedMode', false);
                } else {
                  setValue('hasAdvancedMode', true);
                  setSelectedDominant(el.id);
                  selectPieceWithGranitId(el.id);
                  setHasSelectedAllPieces(false);
                }
              }}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                isSelected
                  ? 'bg-turquoise text-white shadow-sm'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-turquoise hover:bg-turquoise-light'
              }`}
            >
              {el.granit?.reducedImage && (
                <img
                  src={`${buckets?.BUCKET_3D_PIECES || ''}/${
                    el.granit.reducedImage
                  }`}
                  alt={el.granit?.name || 'Texture'}
                  className={`w-5 h-5 rounded-full object-cover ${
                    isSelected ? 'ring-2 ring-white' : 'ring-1 ring-gray-200'
                  }`}
                />
              )}
              <span className='truncate max-w-[120px]'>{el.granit?.name}</span>
            </button>
          );
        })}
      </div>

      {/* Info contextuelle */}
      {hasSelection && (
        <p className='text-xs text-turquoise-dark bg-turquoise-light px-3 py-2 rounded-md'>
          {hasSelectedAllPieces
            ? 'Le granit sélectionné sera appliqué à toutes les pièces compatibles.'
            : `Le granit sélectionné remplacera toutes les pièces en "${
                activeGranits.find((g) => g.id === selectedDominant)?.granit
                  ?.name
              }".`}
        </p>
      )}
    </div>
  );
};
